import { FC, useCallback, useState } from 'react';
import { Button, Image, Input, Space, Switch, Typography, message } from 'antd';
import { useTranslation } from 'react-i18next';
import { SettingsKeys, parseJson } from '@medicusunion/front-utils';
import { useAppSelector } from 'store/hooks';
import { selectUserData } from 'store/user/selectors';
import { useAuthDispatchContext } from 'providers/Auth';
import { generateAuthQr, verifyOtp } from 'services/api/auth';
import { updateMeSettings } from 'services/api/profile';

import { showErrorMessage } from 'utils/showErrorMessage';
import { StyledList, StyledToken } from './styled';
import { jwt } from 'utils/jwt';
import { tokenStorage } from 'utils/storage';

const { Title, Paragraph } = Typography;

const Authentication: FC = () => {
  const { t } = useTranslation();
  const { refetchUserData } = useAuthDispatchContext();
  const user = useAppSelector(selectUserData());
  const [code, setCode] = useState('');
  const [error, setError] = useState(false);
  const [active, setActive] = useState(
    (parseJson(user?.settings) as Record<string, unknown> | null)?.auth2fa ===
      true
  );

  const [result, setResult] = useState<{
    qr: string;
    secretCode: string;
  } | null>(null);

  const writeSettings = useCallback(
    async (value: boolean) => {
      try {
        await updateMeSettings({
          ...(parseJson(user?.settings) || {}),
          [SettingsKeys.auth2fa]: value
        });
        refetchUserData();
      } catch (err) {
        showErrorMessage(err);
      }
    },
    [refetchUserData, user?.settings]
  );

  const onConfirm = useCallback(async () => {
    if (!code) {
      setError(true);

      return;
    }

    try {
      await verifyOtp({ otp: code, token: tokenStorage.get() ?? '' });
      writeSettings(true);
      message.success(t('profile.2fa.success'));
      setResult(null);
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (err: any) {
      if (err?.response?.data?.status === 401) {
        const newToken = await jwt.refreshToken();
        if (newToken) onConfirm();
      } else {
        message.error(err?.response?.data?.message);
      }
    }
  }, [code, t, writeSettings]);

  const onToggleSwitch = useCallback(
    async (val: boolean) => {
      if (val) {
        try {
          const qr = await generateAuthQr();
          setResult({ qr: qr.qr, secretCode: qr.secretKey });
        } catch (err) {
          showErrorMessage(err);
        }
      } else {
        writeSettings(false);
        setResult(null);
      }

      setActive(val);
    },
    [writeSettings]
  );

  return (
    <Space direction="vertical" size={16} style={{ maxWidth: '100%' }}>
      <Space>
        <Switch checked={active} onChange={onToggleSwitch} />
        <Typography.Text>{t('profile.2fa.title')}</Typography.Text>
      </Space>
      {!!result && (
        <>
          <div>
            <Title level={4}>{t('profile.2fa.configuring')}</Title>
            <StyledList>
              <li>{t('profile.2fa.install_app')}</li>
              <li>{t('profile.2fa.add')}</li>
              <li>{t('profile.2fa.select_scan')}</li>
            </StyledList>
          </div>
          <div>
            <Title level={4}>{t('profile.2fa.scan_code')}</Title>
            <Image width={200} height={200} preview={false} src={result.qr} />
          </div>
          <div>
            <Title level={4}>{t('profile.2fa.enter_code')}</Title>
            <StyledToken copyable code>
              {result.secretCode}
            </StyledToken>
          </div>
          <div>
            <Title level={4}>{t('profile.2fa.verify_code')}</Title>
            <Paragraph>{t('profile.2fa.verify')}</Paragraph>
            <Input
              value={code}
              onChange={e => setCode(e.target.value)}
              status={error ? 'error' : ''}
            />
          </div>
          <Button onClick={onConfirm} block type="primary">
            Confirm
          </Button>
        </>
      )}
    </Space>
  );
};

export default Authentication;
